import { boundMethod } from 'autobind-decorator';
import { toDate, format } from 'date-fns';
import statuses from 'statuses';

const ENTRY_DEFAULT_INTERVAL_TIME = 2000;
const ENTRY_DEFAULT_MAX_ATTEMPTS = 5;

class ProductFeedsJobs {
  constructor(appAPI, appUtils, productFeedsJobsListManager) {
    'inject';

    this._appAPI = appAPI;
    this._appUtils = appUtils;
    this._productFeedsJobsListManager = productFeedsJobsListManager;
  }
  cancelJob(accountId, jobId) {
    return this._appAPI.cancelProductFeedJob(accountId, jobId);
  }
  createJob(accountId, variant, mediaNumber) {
    return this._appAPI.createProductFeedsJob(
      accountId,
      variant,
      mediaNumber,
    )
    .then((job) => this._jobFormatter(job));
  }
  getFailedJobs() {
    return this._appAPI.getFailedProductFeedsJobs()
    .then(({ jobs }) => jobs.map(this._jobFormatter));
  }
  getJob(accountId, jobId) {
    return this._appAPI.getProductFeedsJob(accountId, jobId)
    .then((job) => this._jobFormatter(job));
  }
  getJobEntries(
    accountId,
    jobId,
    fromEntry,
    deleteCurrentEntry,
    wait = false,
    intervalTime = ENTRY_DEFAULT_INTERVAL_TIME,
    maxAttempts = ENTRY_DEFAULT_MAX_ATTEMPTS,
  ) {
    return this._getJobEntries(
      accountId,
      jobId,
      fromEntry,
      deleteCurrentEntry,
      wait,
      intervalTime,
      maxAttempts,
      1,
    );
  }
  getJobLog(accountId, jobId) {
    return this._appAPI.getProductFeedsJobLog(accountId, jobId);
  }
  getJobVariants(accountId) {
    return this._appAPI.getProductFeedsJobVariants(accountId)
    .then(({ variants }) => variants);
  }
  newList(accountId) {
    return this._productFeedsJobsListManager.newList(accountId, this._jobFormatter);
  }
  _delayedPromise(time) {
    return new Promise((resolve) => {
      setTimeout(() => resolve(), time);
    });
  }
  _getJobEntries(
    accountId,
    jobId,
    fromEntry,
    deleteCurrentEntry,
    wait,
    intervalTime,
    maxAttempts,
    attempt,
  ) {
    return this._appAPI.getProductFeedsJobEntries(
      accountId,
      jobId,
      fromEntry,
      attempt,
    )
    .then((response) => {
      const { status } = response.job_execution;
      const result = {
        status,
        entry: fromEntry,
        done: status !== 'running',
        log: '',
      };

      const newEntries = Object.assign({}, response.entries);
      if (deleteCurrentEntry) {
        delete newEntries[fromEntry];
      }
      const keys = Object.keys(newEntries);
      if (keys.length) {
        result.entry = Number(keys[keys.length - 1]);
        result.log = keys.map((key) => newEntries[key]).join('\n');
      }

      return result;
    })
    .catch((error) => {
      let result;
      if (wait && error.status === statuses('not found')) {
        if (attempt < maxAttempts) {
          result = this._delayedPromise(intervalTime)
          .then(() => this._getJobEntries(
            accountId,
            jobId,
            fromEntry,
            deleteCurrentEntry,
            wait,
            intervalTime,
            maxAttempts,
            attempt + 1,
          ));
        } else {
          result = Promise.reject(new Error('Unable to get the job entry'));
        }
      } else {
        result = Promise.reject(error);
      }

      return result;
    });
  }
  @boundMethod
  _jobFormatter(job) {
    const millisecondsInASecond = 1000;
    const timestamp = toDate(Number(job.timestamp * millisecondsInASecond));
    const title = format(timestamp, 'MMM do yy HH:mm:ss');
    const [id] = job.filename.split('.');
    return Object.assign({}, job, { id, title });
  }
}

export { ProductFeedsJobs };
